// Host-tunable knobs for the liveTsx placeholder. Today this is just
// the "what does 100% look like" hint that drives
// `GeneratingVisualization`'s bar fill — surfaces whose fences tend to
// be longer (e.g. Composer with full-dashboard snippets) should bump
// this up so the bars don't saturate halfway through, while surfaces
// with shorter snippets (DataExplorer) can stick to the default. The
// server's per-tick stride is independent of this; only the client
// mapping from `tickCount` to a 0..1 progress fraction is affected.

import React, { createContext, useContext, useMemo } from 'react';

export const LIVE_TSX_DEFAULT_MAX_CHARS_FOR_FULL = 3000;

const LiveTsxConfigContext = createContext<number>(LIVE_TSX_DEFAULT_MAX_CHARS_FOR_FULL);

/**
 * Read the host-configured "100%" cap for placeholder progress. Falls
 * back to {@link LIVE_TSX_DEFAULT_MAX_CHARS_FOR_FULL} when no provider
 * is mounted above. Consumed inside `LlmResponseRender` and forwarded
 * to `parseContentBlocks`.
 */
export function useLiveTsxMaxCharsForFull(): number {
    return useContext(LiveTsxConfigContext);
}

/**
 * Wraps any subtree that renders `LlmResponseRender` with a custom
 * `maxCharsForFull`. Place near the top of a view (e.g. ComposerViewV2)
 * so every chat surface inside picks up the same hint.
 */
export const LiveTsxConfigProvider: React.FC<{
    maxCharsForFull?: number;
    children: React.ReactNode;
}> = ({ maxCharsForFull, children }) => {
    const value = useMemo(
        () => maxCharsForFull ?? LIVE_TSX_DEFAULT_MAX_CHARS_FOR_FULL,
        [maxCharsForFull],
    );
    return (
        <LiveTsxConfigContext.Provider value={value}>
            {children}
        </LiveTsxConfigContext.Provider>
    );
};
